import { keyPressHelper } from '@brandfolder/utilities';
import React, {
  useEffect,
  useRef,
  FunctionComponent,
  KeyboardEvent,
  LiHTMLAttributes,
  SyntheticEvent,
  ReactNode,
  CSSProperties,
} from 'react';
import classnames from 'classnames';

import { BaseDropdownItemProps } from '@components/library/dropdown_item/DropdownItemProps';
import { FontIcon, FontIcons } from '@components/library/icon';

const baseName = 'dropdown-item';

const BaseDropdownItem: FunctionComponent<
BaseDropdownItemProps & LiHTMLAttributes<HTMLLIElement>
> = (props) => {
  const {
    children,
    className,
    icon = '',
    iconSize,
    id,
    indentation = 0,
    isArrowed,
    isDisabled = false,
    isDisplayed = true,
    isEmpty = false,
    isSelected,
    isTabbable,
    isWarningItem = false,
    name,
    onChoose,
    suppressScrollIntoView = false,
    styleClassNames = {}, // default value for testing (required prop)
    variantName = '', // default value for testing (required prop)
    ...liProps
  } = props;

  const itemRef = useRef<HTMLLIElement | null>(null);
  const isActionItem = variantName === 'action';
  // Menu items use roving tabindex (APG): keep them out of the tab order; focus via arrows.
  const resolvedIsTabbable = isTabbable ?? !isActionItem;

  // Allows for negative values
  // Negative values will result in no indentation displayed (same as 0)
  // Values greather than 10 result in indentation of 10
  const indentationConstrained = indentation > 10 ? 10 : indentation;

  useEffect(() => {
    if (suppressScrollIntoView || !isArrowed || !itemRef.current) {
      return;
    }

    // Scroll only within the popup listbox/menu. Element.scrollIntoView() can scroll
    // ancestor containers (including the page behind a modal), which causes screen
    // readers to announce large amounts of unrelated content when a combobox opens.
    const item = itemRef.current;
    const container = item.closest('[role="listbox"], [role="menu"]') as HTMLElement | null;
    if (!container) {
      return;
    }

    const itemRect = item.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    if (itemRect.bottom > containerRect.bottom) {
      container.scrollTop += itemRect.bottom - containerRect.bottom;
    } else if (itemRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - itemRect.top;
    }
  }, [isArrowed, suppressScrollIntoView]);

  const onKeyPressHandler = keyPressHelper(onChoose);

  const activateItem = (e: SyntheticEvent): void => {
    if (isDisabled || !onChoose) return;
    onChoose(name, e);
  };

  const focusSiblingMenuItem = (
    current: HTMLLIElement,
    direction: 'next' | 'previous' | 'first' | 'last',
  ): void => {
    const menu = current.closest('[role="menu"]');
    if (!menu) return;

    const items = Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]'))
      .filter((item) => item.getAttribute('aria-disabled') !== 'true');
    if (!items.length) return;

    const currentIndex = items.indexOf(current);
    let nextIndex = 0;
    if (direction === 'next') {
      nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
    } else if (direction === 'previous') {
      nextIndex = currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
    } else if (direction === 'last') {
      nextIndex = items.length - 1;
    }

    items[nextIndex]?.focus();
  };

  if (!isDisplayed) return null;

  const renderIcon = (): ReactNode => {
    if (!icon) return null;

    const inlineIconStyle: CSSProperties = iconSize
      ? { fontSize: `${iconSize}px` }
      : undefined;

    if (icon.includes('bff-')) {
      return (
        <span
          aria-hidden
          className={`${icon} ${styleClassNames.icon || ''}`}
          role="img"
          style={inlineIconStyle}
        />
      );
    }
    return (
      <FontIcon
        aria-hidden
        className={styleClassNames.icon}
        icon={icon as FontIcons}
        style={inlineIconStyle}
      />
    );
  };

  const itemRole = isActionItem ? 'menuitem' : 'option';

  return (
    <li
      {...liProps}
      ref={itemRef}
      aria-disabled={isDisabled || undefined}
      aria-selected={!isActionItem ? Boolean(isSelected) : undefined}
      className={classnames(
        `bf-${baseName}__${variantName}`, // base-variant class for post consumption styling
        styleClassNames[variantName], // class for css modules
        className, // user specified class
        { [styleClassNames[`${variantName}-warning`]]: isWarningItem },
        { [styleClassNames.selected]: isSelected },
        { [styleClassNames.arrowed]: isArrowed },
        { [styleClassNames.disabled]: isDisabled },
        { [styleClassNames.empty]: isEmpty },
        { [styleClassNames[`indented${indentationConstrained}`]]: indentationConstrained }
      )}
      id={id || name?.toString()}
      onClick={(e: SyntheticEvent): void => {
        activateItem(e);
      }}
      onKeyDown={(e: KeyboardEvent<HTMLLIElement>): void => {
        if (!isActionItem || isDisabled) return;

        switch (e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault();
            e.stopPropagation();
            activateItem(e);
            break;
          case 'ArrowDown':
            e.preventDefault();
            e.stopPropagation();
            focusSiblingMenuItem(e.currentTarget, 'next');
            break;
          case 'ArrowUp':
            e.preventDefault();
            e.stopPropagation();
            focusSiblingMenuItem(e.currentTarget, 'previous');
            break;
          case 'Home':
            e.preventDefault();
            e.stopPropagation();
            focusSiblingMenuItem(e.currentTarget, 'first');
            break;
          case 'End':
            e.preventDefault();
            e.stopPropagation();
            focusSiblingMenuItem(e.currentTarget, 'last');
            break;
          default:
            break;
        }
      }}
      onKeyPress={(e: KeyboardEvent<HTMLLIElement>): void => {
        onKeyPressHandler(e, name);
      }}
      role={itemRole}
      tabIndex={resolvedIsTabbable ? 0 : -1}
    >
      {icon && renderIcon()}
      {children}
    </li>
  );
};

export default BaseDropdownItem;
